export interface ElapsedInput {
  /** Recording start, ms since epoch. */
  startedAt: number;
  /** Current wall-clock, ms since epoch. */
  now: number;
  /** When the current pause began (ms since epoch), or null while recording. */
  pausedAt: number | null;
  /** Total paused time from already-completed pause intervals, in ms. */
  accumulatedPausedMs: number;
}

/**
 * Elapsed *recording* time in ms, excluding paused intervals. While paused, the
 * in-progress pause (`now - pausedAt`) cancels the growing wall-clock so the
 * value holds steady; on resume the consumer folds that span into
 * `accumulatedPausedMs` and clears `pausedAt`. Clamped to ≥ 0.
 */
export function computeElapsedMs({ startedAt, now, pausedAt, accumulatedPausedMs }: ElapsedInput): number {
  const currentPause = pausedAt !== null ? now - pausedAt : 0;
  return Math.max(0, now - startedAt - accumulatedPausedMs - currentPause);
}

/**
 * Format elapsed milliseconds as "M:SS" or "H:MM:SS" once past 1 hour.
 * Handles negative values defensively (returns "0:00").
 */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) {
    const mm = String(minutes).padStart(2, "0");
    return `${hours}:${mm}:${ss}`;
  }
  return `${minutes}:${ss}`;
}
